import { ChevronDown } from 'lucide-react';
import {
  Button as AriaButton,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
} from 'react-aria-components';
import styles from './SelectField.module.css';

interface SelectFieldProps<T extends string> {
  label: string;
  value: T;
  options: readonly (readonly [T, string])[];
  onChange: (value: T) => void;
  isDisabled?: boolean;
  /** Compact toolbar trigger: the label sits inside the button, before the value. */
  inline?: boolean;
  /** Keeps the label for assistive tech only (a role picker in a member row). */
  hideLabel?: boolean;
}

export function SelectField<T extends string>({
  label,
  value,
  options,
  onChange,
  isDisabled = false,
  inline = false,
  hideLabel = false,
}: SelectFieldProps<T>) {
  return (
    <Select
      className={styles.field}
      data-inline={inline || undefined}
      selectedKey={value}
      isDisabled={isDisabled}
      onSelectionChange={(key) => {
        if (key !== null) onChange(String(key) as T);
      }}
    >
      <Label className={inline || hideLabel ? 'sr-only' : styles.label}>{label}</Label>
      <AriaButton className={styles.trigger}>
        {inline && (
          <span className={styles.inlineLabel} aria-hidden="true">
            {label}
          </span>
        )}
        <SelectValue className={styles.value} />
        <ChevronDown size={inline ? 14 : 18} aria-hidden="true" />
      </AriaButton>
      <Popover className={styles.popover} offset={4}>
        <ListBox className={styles.list}>
          {options.map(([id, text]) => (
            <ListBoxItem key={id} id={id} className={styles.item}>
              {text}
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </Select>
  );
}
